import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import i18next from "src/i18n/config";
import { RootState } from "src/store";
import { useCurrentWorkspaceEvents } from "src/store/api/events/helpers";
import { EventLevelHeader } from "src/ui/shared/table/headers/EventLevelHeader";
import { makeStyles } from "tss-react/mui";

import { showDetails } from "@dashboard/devices/store/index";
import { selectDevicesByGroupId } from "@dashboard/devices/store/selectors/devices";
import { EventReport, Group } from "@dashboard/devices/types";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { selectLicensesEntities } from "@dashboard/licenses/store/selectors";
import { renderEventContent } from "@dashboard/shared/components/events";
import {
    Box,
    Collapse,
    IconButton,
    Paper,
    TableCell,
    TableContainer,
    TableRow,
    Theme,
    Typography,
    useTheme,
} from "@mui/material";
import { useFetchBuildsForProductsQuery } from "@products/api";
import { useCurrentWorkspaceProducts } from "@products/api/helpers";
import TimeService from "@services/timeService/timeService";
import { MinimalTable, MinimalTableCell, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { Tooltip } from "@shared/Tooltip";
import {
    ColumnDef,
    ColumnFiltersState,
    ExpandedState,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";

import { useDeviceNameClickHandler } from "../../shared/EventTab/EventTab.utils";
import { PaneElement } from "../../shared/PaneElement";

const useStyles = makeStyles()((t: Theme) => ({
    content: {
        maxHeight: "100%",
        marginBottom: t.spacing(7),
    },
    expandedContent: {
        padding: t.spacing(2),
        paddingLeft: t.spacing(4),
    },
}));

const createColumns = (
    getDeviceName: (event: EventReport) => string,
    getEventDetails: (event: EventReport) => { color: string; header: string; component: React.ReactNode },
): ColumnDef<EventReport, unknown>[] => [
    {
        id: "expander",
        header: () => null,
        cell: ({ row }) => (
            <MinimalTableCell>
                <IconButton
                    size="large"
                    onClick={(e) => {
                        e.stopPropagation();
                        row.toggleExpanded();
                    }}
                >
                    {row.getIsExpanded() ? (
                        <i className="fa-solid fa-chevron-down" style={{ fontSize: 16 }} />
                    ) : (
                        <i className="fa-solid fa-chevron-right" style={{ fontSize: 16 }} />
                    )}
                </IconButton>
            </MinimalTableCell>
        ),
        enableSorting: false,
    },
    {
        header: i18next.t("sidepanels:groupPanel.table.header.date"),
        accessorKey: "created",
        enableSorting: true,
        cell: (info) => {
            const dateTime = TimeService.getLocalDate(info.row.original.created, "ISO", "dateTime");
            const date = TimeService.getLocalDate(info.row.original.created, "ISO", "date");
            return (
                <MinimalTableCell>
                    <Tooltip title={dateTime}>
                        <Typography variant="body1">{date}</Typography>
                    </Tooltip>
                </MinimalTableCell>
            );
        },
    },
    {
        id: "eventLevel",
        header: () => i18next.t("common:label.type"),
        accessorFn: (row) => {
            return row.level;
        },
        filterFn: (row, _columnId, filterValue: string[]) => {
            return filterValue.includes(String(row.original.level));
        },
        cell: ({ row }) => {
            const level =
                row.original.level === "warning"
                    ? i18next.t("common:table.filter.warning")
                    : i18next.t("common:table.filter.notice");
            return (
                <MinimalTableCell>
                    <Typography variant="body1">{level}</Typography>
                </MinimalTableCell>
            );
        },
        enableSorting: false,
    },
    {
        header: i18next.t("common:label.event"),
        accessorKey: "eventTypeName",
        enableSorting: true,
        cell: (info) => {
            const eventDetails = getEventDetails(info.row.original);
            return (
                <MinimalTableCell>
                    <Typography variant="body1">{eventDetails.header}</Typography>
                </MinimalTableCell>
            );
        },
    },
    {
        header: i18next.t("common:label.device"),
        accessorKey: "entityId",
        enableSorting: true,
        cell: (info) => {
            const isGroupEvent = info.row.original.eventTypeName.startsWith("GroupEvent.");
            const deviceName = getDeviceName(info.row.original);
            return (
                <MinimalTableCell>
                    {isGroupEvent ? (
                        <Tooltip title={i18next.t("sidepanels:groupPanel.table.item.notApplicableForDevices")}>
                            <Typography variant="body1">-</Typography>
                        </Tooltip>
                    ) : (
                        <Typography variant="body1">{deviceName}</Typography>
                    )}
                </MinimalTableCell>
            );
        },
    },
];

const EventTab = (props: Group) => {
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
    const [expanded, setExpanded] = React.useState<ExpandedState>({});

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation(["sidepanels", "common"]);
    const { classes } = useStyles();
    const theme = useTheme();
    const allGroups = useSelector(selectGroups);

    const { data: products = [] } = useCurrentWorkspaceProducts();
    const { data: builds = [] } = useFetchBuildsForProductsQuery(products.map((product) => product.id));

    const { data: eventReports, isLoading } = useCurrentWorkspaceEvents({
        groupIds: [props.id],
        allWorkspaceGroupsLength: allGroups.length,
    });
    const data = React.useMemo(() => eventReports?.list || [], [eventReports?.list]);

    const devices = useSelector((state: RootState) => selectDevicesByGroupId(state, props.id));
    const licensesEntities = useSelector((state: RootState) => selectLicensesEntities(state));

    const getDeviceName = (event: EventReport) => {
        const isLicenseEvent = event.eventTypeName.startsWith("LicenseEvent.");
        const license = isLicenseEvent ? licensesEntities[parseInt(event.entityId)] : undefined;
        const device = devices.find((d) => d?.id.toString() === event.entityId) || (license?.device ? license.device : undefined);
        return device?.name ?? t("sidepanels:groupPanel.table.item.unknownDevice");
    };

    const handleDeviceNameClick = useDeviceNameClickHandler({
        devices: devices,
        licensesEntities,
        groupId: props.id,
    });

    const getEventDetails = (event: EventReport) => {
        const isLicenseEvent = event.eventTypeName.startsWith("LicenseEvent.");
        const license = isLicenseEvent ? licensesEntities[parseInt(event.entityId)] : undefined;
        const device = devices.find((d) => d?.id.toString() === event.entityId) || (license?.device ? license.device : undefined);

        const handleLicenseClickForEvent = license
            ? () => {
                  dispatch(
                      showDetails({
                          type: "license",
                          selectedId: license.id,
                      }),
                  );
              }
            : undefined;

        const result = renderEventContent(
            event,
            {
                license,
                device,
                navigate,
                group: props,
                allGroups,
                products,
                builds,
                handleDeviceNameClick,
                handleLicenseClick: handleLicenseClickForEvent,
            },
            theme,
        );

        return {
            color: result.color,
            header: result.header,
            component: result.component,
        };
    };

    const columns = createColumns(getDeviceName, getEventDetails);

    const table = useReactTable<EventReport>({
        data,
        columns,
        state: { columnFilters, expanded },
        onExpandedChange: setExpanded,
        getRowCanExpand: () => true,
        getSubRows: () => undefined,
        initialState: {
            sorting: [{ id: "created", desc: true }],
            pagination: {
                pageSize: 12,
                pageIndex: 0,
            },
        },
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const handleResetFilter = () => {
        setColumnFilters([]);
        setExpanded({});
        table.setPageIndex(0);
    };

    const { pageSize, pageIndex } = table.getState().pagination;

    return (
        <PaneElement.Container>
            <PaneElement.Header>{t("common:label.events")}</PaneElement.Header>
            <Box className={classes.content}>
                <TableContainer component={Paper}>
                    <MinimalTable>
                        <MinimalTableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <Header key={header.id} {...header}>
                                            {header.id === "eventLevel" && (
                                                <EventLevelHeader
                                                    columnFilters={columnFilters}
                                                    setColumnFilters={setColumnFilters}
                                                />
                                            )}
                                        </Header>
                                    ))}
                                </TableRow>
                            ))}
                        </MinimalTableHeader>
                        <TableBody
                            table={table}
                            filter={{ isActive: !!columnFilters.length, onResetFilter: handleResetFilter }}
                            loading={isLoading}
                            noDataMessage={t("sidepanels:groupPanel.table.noEvents")}
                        >
                            {table.getRowModel().rows.map((row) => {
                                const eventDetails = getEventDetails(row.original);
                                return (
                                    <React.Fragment key={row.original.id}>
                                        <TableRow>
                                            {row.getVisibleCells().map((cell) => (
                                                <React.Fragment key={cell.id}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </React.Fragment>
                                            ))}
                                        </TableRow>
                                        <TableRow>
                                            <TableCell colSpan={columns.length} style={{ borderBottom: "none", padding: 0 }}>
                                                <Collapse in={row.getIsExpanded()} timeout="auto" unmountOnExit>
                                                    <Box className={classes.expandedContent}>
                                                        <Typography variant="body1" component="div">
                                                            {eventDetails.component}
                                                        </Typography>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </MinimalTable>
                </TableContainer>
                <CustomTablePagination
                    component="div"
                    rowsPerPageOptions={[12, 24, 48]}
                    count={table.getFilteredRowModel().rows.length}
                    rowsPerPage={pageSize}
                    page={pageIndex}
                    onPageChange={(_event, page) => table.setPageIndex(page)}
                    onRowsPerPageChange={(e) => {
                        const size = e.target.value ? Number(e.target.value) : 12;
                        table.setPageSize(size);
                    }}
                />
            </Box>
        </PaneElement.Container>
    );
};

export default EventTab;
